import { useCallback, useContext, useDebugValue } from 'react';
import * as Utils from '@root/lib/services/UtilService';
import * as LogicService from '@root/lib/services/LogicService';
import * as PREService from '@root/lib/services/PREService';
import context from '.';

/**
 * Gets meals allowed for the provided order date and box id. Uses the isLocal variable to determine
 * what menu should be retrieved
 * 
 * @type {(
  *   boxId: number,
  *   orderDate: Date,
  *   isLocal: boolean,
  * ) => Promise<[
  *   Array,
  *   Object,
  * ]>}
  */
const getMeals = async (boxId, orderDate, isLocal) => {
  return await LogicService.getMealsForOrderDate(boxId, orderDate, isLocal);
}

/**
 * @return{[
 *   Array<Object>,
 *   Object,
 * ]}
 */
const useMeals = () => {
  const { state, setState, stateRef } = useContext(context);
  const { meals } = state;

  /**
   * Sets the loading flag for meals
   * 
   * @type {(flag: boolean) => void}
   */
  const setLoading = useCallback((flag) => {
    Utils.setNestedImmutably('meals.loading', flag).setWith(setState);
  }, []);

  /**
   * Runs the meals through PRE before setting the meals
   * 
   * @type {(meals: Array, timeSlot: Object) => Promise<void>}
   */
  const setMealsWithTimeSlot = useCallback((meals, timeSlot) => {
    Utils.setNestedImmutably('meals', {
      state: meals,
      timeSlot,
    }, true).setWith(setState);
  }, []);

  /**
   * Gets the timeslot from state
   */
  const getTimeSlot = useCallback(() => {
    return meals.timeSlot;
  }, [meals.timeSlot]);

  /**
   * Returns true if the meals are loading, false otherwise
   */
  const isLoading = useCallback(() => {
    const { meals } = stateRef.current;
    return meals.loading;
  }, []);

  useDebugValue(meals.state);
  return [
    meals.state,
    {
      processMeals: PREService.processMeals,
      getMeals,
      setMealsWithTimeSlot,
      getTimeSlot,
      isLoading,
      setLoading,
    },
  ];
}

export default useMeals;